import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useMe, useSubscription, useLogout, usePartnerRepStatus } from "../hooks/useApi";
import { PLAN_LABELS } from "../utils/plans";
import { getDisplayName, getInitial } from "../utils/displayName";
import ThemeToggle from "./ThemeToggle";
import NotificationBell from "./NotificationBell";
import { useTheme } from "../hooks/useTheme";

const drawerItems = [
  {
    to: "/profile",
    label: "پروفایل",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21v-1a8 8 0 0116 0v1" />
      </svg>
    ),
  },
  {
    to: "/subscription",
    label: "اشتراک",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 3h12l4 6-10 13L2 9z" />
        <path d="M11 3L8 9l4 13 4-13-3-6" />
        <path d="M2 9h20" />
      </svg>
    ),
  },
  {
    to: "/support",
    label: "پشتیبانی",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 14v-3a9 9 0 0118 0v3" />
        <path d="M21 14h-3a2 2 0 00-2 2v3a2 2 0 002 2h1a2 2 0 002-2v-5z" />
        <path d="M3 14h3a2 2 0 012 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-5z" />
      </svg>
    ),
  },
];

const repDrawerItem = {
  to: "/partners/dashboard",
  label: "داشبورد همکاران",
  icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m11 17 2 2a1 1 0 1 0 3-3" />
      <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
      <path d="m21 3 1 11h-2" />
      <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
      <path d="M3 4h8" />
    </svg>
  ),
};

export default function MobileHeader() {
  const { data: user } = useMe();
  const { data: subscription } = useSubscription();
  const { data: repStatus } = usePartnerRepStatus();
  const { isDark } = useTheme();
  const logout = useLogout();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const displayName = getDisplayName(user);
  const initial = getInitial(user);

  const current = subscription?.current;
  const planName = current?.plan_name || "free";
  const planLabel = PLAN_LABELS[planName] || planName;
  const isFree = !current?.end_date;
  const fixed = subscription?.current_fixed;
  const fixedPlanName = fixed?.plan_name;

  const daysLeft = current?.end_date
    ? Math.max(0, Math.ceil((new Date(current.end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const subline = (isFree ? "پلن رایگان" : `${planLabel} | ${daysLeft} روز باقی`) +
    (fixedPlanName ? ` (${fixedPlanName})` : "");

  return (
    <header className="md:hidden fixed top-0 inset-x-0 z-40">
      {/* Top bar */}
      <div className="flex flex-row-reverse items-center justify-between px-4 py-3 bg-surface/85 backdrop-blur-md border-b border-line">
        {/* Logo */}
        <a href="/dashboard" className="flex flex-row-reverse items-center shrink-0">
          <img src="/logo-reverse-filled.svg" alt="ASHA-AI" className="w-8 h-8" />
        </a>

        <div className="flex items-center gap-2">
          {/* Notifications */}
          <NotificationBell />

          {/* Avatar & Info */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2 cursor-pointer min-w-0"
          >
            <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold shrink-0">
              {initial}
            </div>
            <div className="text-right leading-tight min-w-0">
              <div className="text-text-main font-medium text-[14px] max-w-[150px] truncate">{displayName}</div>
              <div className="text-text-secondary text-[12px] max-w-[150px] truncate">{subline}</div>
            </div>
          </button>
        </div>
      </div>

      {/* Drawer */}
      {open && (
        <>
          <div className="fixed inset-0" onClick={() => setOpen(false)} />
          <div
            className="glass absolute top-full right-2 left-2 sm:right-3 sm:left-auto sm:w-64 mt-2 rounded-[12px] overflow-hidden animate-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="py-1.5 flex flex-col">
              {repStatus?.is_representative && (
                <NavLink
                  key={repDrawerItem.to}
                  to={repDrawerItem.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-body transition-colors ${
                      isActive ? "text-primary bg-primary-light/40" : "text-text-main hover:bg-surface-hover"
                    }`
                  }
                >
                  <span className="text-text-secondary shrink-0">{repDrawerItem.icon}</span>
                  {repDrawerItem.label}
                </NavLink>
              )}
              {drawerItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-body transition-colors ${
                      isActive ? "text-primary bg-primary-light/40" : "text-text-main hover:bg-surface-hover"
                    }`
                  }
                >
                  <span className="text-text-secondary shrink-0">{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="flex items-center gap-3 text-body text-text-main">
                  <span className="text-text-secondary shrink-0">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                    </svg>
                  </span>
                  {isDark ? "حالت روشن" : "حالت تاریک"}
                </span>
                <ThemeToggle />
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  logout.mutate();
                }}
                disabled={logout.isPending}
                className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-body text-text-main hover:bg-surface-hover active:bg-surface-hover transition-colors text-right disabled:opacity-60"
              >
                <span className="text-text-secondary shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </span>
                خروج
              </button>
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
